/**
 * parseLibraryFile.js
 * 
 * Turns an uploaded spreadsheet (Excel, Numbers or CSV) into a library.
 * 
 * Expected layout: the first column holds the attribute names, and every other column is an item:
 * 
 *      Player   | Messi | Ronaldo | ...
 *      Country  | ARG   | POR     | ...
 *      Points   | 95    | 94      | ...
 * 
 * The usual layout (attribute names across the first row, one item per row) also works.
 * One attribute must be called Rating, Points or Marks; that's what teams are judged on.
 * The first other attribute names each item.
 */

import { read, utils } from "xlsx";
import { SCORE_ATTRIBUTE_PATTERN } from "./libraries";

export const ACCEPTED_FILE_TYPES = ".xlsx,.xls,.numbers,.csv,.ods";
export const MAX_ITEMS = 500;
export const MAX_ATTRIBUTES = 20;
const MAX_CATEGORY_VALUES = 30;
const MAX_ERRORS_SHOWN = 5;

const isEmpty = (value) => value === undefined || value === null || String(value).trim() === "";

/**
 * Reads a number from a cell, allowing things like "8.5", "1,200" or "90%"
 * @returns {Number|null}   -   The number, or null
 */
const toNumber = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const cleaned = String(value ?? "").replace(/[,%\s]/g, "");
    if (cleaned === "") return null;
    const number = Number(cleaned);
    return Number.isFinite(number) ? number : null;
};

const transpose = (matrix) => {
    const width = Math.max(...matrix.map((row) => row.length));
    return Array.from({ length: width }, (_, column) => matrix.map((row) => row[column] ?? ""));
};

/**
 * Removes rows and columns that are completely empty
 */
const trimMatrix = (matrix) => {
    const rows = matrix.filter((row) => row.some((cell) => !isEmpty(cell)));
    if (rows.length === 0) return [];
    const columns = transpose(rows).filter((column) => column.some((cell) => !isEmpty(cell)));
    return columns.length === 0 ? [] : transpose(columns);
};

/**
 * Works out what kind of values an attribute holds
 * @returns {String}    -   'number' | 'category' | 'text'
 */
const inferKind = (values) => {
    const filled = values.filter((value) => !isEmpty(value));
    if (filled.length === 0) return "text";
    if (filled.every((value) => toNumber(value) !== null)) return "number";
    const distinct = new Set(filled.map((value) => String(value).trim())).size;
    return distinct < filled.length && distinct <= MAX_CATEGORY_VALUES ? "category" : "text";
};

/**
 * Parses a spreadsheet file into a library (without name, description or code).
 * @param {File} file   -   The uploaded file
 * @returns {Object}    -   { nameAttribute, scoreAttribute, attributes, items, itemCount, warnings }
 * @throws {Error}      -   With a message to show the user if the file can't be used
 */
export const parseLibraryFile = async (file) => {
    let workbook;
    try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        // CSV is plain text: decode it as UTF-8 so names like "Mbappé" survive.
        // Everything else (Excel, Numbers, ODS) is read as raw bytes.
        workbook = /\.csv$/i.test(file.name || "")
            ? read(new TextDecoder("utf-8").decode(bytes), { type: "string" })
            : read(bytes, { type: "array" });
    } catch (error) {
        throw new Error("Couldn't read that file. Upload an Excel, Numbers or CSV file.");
    }

    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const matrix = trimMatrix(utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" }));
    if (matrix.length < 2) throw new Error("The file needs attribute names and at least two items.");

    // Attribute names go down the first column; fall back to across the first row
    const firstColumn = matrix.map((row) => String(row[0] ?? "").trim());
    const firstRow = matrix[0].map((cell) => String(cell ?? "").trim());
    let table;
    if (firstColumn.some((name) => SCORE_ATTRIBUTE_PATTERN.test(name)))     table = transpose(matrix);
    else if (firstRow.some((name) => SCORE_ATTRIBUTE_PATTERN.test(name)))   table = matrix;
    else throw new Error("One attribute must be called Rating, Points or Marks. That's how teams are judged.");

    const headers = table[0].map((cell) => String(cell ?? "").trim());
    const rows = table.slice(1);

    // --- Attributes ---
    if (headers.some((header) => header === "")) throw new Error("Every attribute needs a name. One of them is blank.");
    if (new Set(headers.map((header) => header.toLowerCase())).size !== headers.length) throw new Error("Two attributes have the same name.");
    if (headers.length < 2) throw new Error("The file needs a name attribute and a Rating, Points or Marks attribute.");
    if (headers.length > MAX_ATTRIBUTES) throw new Error(`A library can have at most ${MAX_ATTRIBUTES} attributes.`);

    const warnings = [];
    const scoreAttributes = headers.filter((header) => SCORE_ATTRIBUTE_PATTERN.test(header));
    const scoreAttribute = scoreAttributes[0];
    if (scoreAttributes.length > 1) warnings.push(`Several scoring attributes found. Teams will be judged on "${scoreAttribute}".`);
    const nameAttribute = headers.find((header) => header !== scoreAttribute);
    const scoreIndex = headers.indexOf(scoreAttribute);
    const nameIndex = headers.indexOf(nameAttribute);

    // --- Items ---
    if (rows.length < 2) throw new Error("A library needs at least two items.");
    if (rows.length > MAX_ITEMS) throw new Error(`A library can have at most ${MAX_ITEMS} items. This one has ${rows.length}.`);

    const errors = [];
    const items = rows.map((row, index) => {
        const label = `Item ${index + 1}`;
        const name = String(row[nameIndex] ?? "").trim();
        const score = toNumber(row[scoreIndex]);
        if (!name)          errors.push(`${label} has no ${nameAttribute}.`);
        if (score === null) errors.push(`${name || label} has no number for ${scoreAttribute}.`);

        const values = {};
        headers.forEach((header, column) => {
            if (column === nameIndex || column === scoreIndex) return;
            const cell = row[column];
            values[header] = typeof cell === "number" ? cell : String(cell ?? "").trim();
        });
        return { name, score, values };
    });

    if (errors.length > 0) {
        const more = errors.length > MAX_ERRORS_SHOWN ? ` (and ${errors.length - MAX_ERRORS_SHOWN} more)` : "";
        throw new Error(errors.slice(0, MAX_ERRORS_SHOWN).join(" ") + more);
    }

    const names = items.map((item) => item.name.toLowerCase());
    if (new Set(names).size !== names.length) warnings.push(`Some items share the same ${nameAttribute}.`);

    const attributes = headers.map((header, column) => ({
        name    :   header,
        kind    :   column === nameIndex ? "name"
                  : column === scoreIndex ? "score"
                  : inferKind(rows.map((row) => row[column])),
    }));

    return { nameAttribute, scoreAttribute, attributes, items, itemCount: items.length, warnings };
};
